/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneBuildList.tsx
 * FILE_ID: JOBHUB-QA-PHONE-BUILD-001
 * PURPOSE: Phone Build body. Cards by default, the table behind the toggle.
 * LANDMINE: the table is NOT deprecated. Both views edit through the same
 *   editor hook, so neither can be removed as "the old one".
 * LANDMINE: Add checklist lives in the More sheet, not in this body.
 */
import { QaChecklistTable } from '../../../qa/QaChecklistTable'
import type { JobQaTabController } from '../useJobQaTabController'
import { QA_PHONE_CONTROLS } from './qaPhoneControlInventory'
import { QaPhoneBuildCards } from './build/QaPhoneBuildCards'
import type { ContextMenuItem } from '@quotech/ui'
import type { QaBuildView } from './useQaPhoneUi'

export function QaPhoneBuildList({
  controller,
  view = 'cards',
  checklistMenuItems,
  renameChecklistId,
  onRenameChecklistStarted,
}: {
  controller: JobQaTabController
  view?: QaBuildView
  checklistMenuItems?: (id: string, name: string, areaId: string) => ContextMenuItem[]
  renameChecklistId?: string | null
  onRenameChecklistStarted?: () => void
}) {
  const { api, client, selection, workspace } = controller
  const execution = workspace.areaExecQuery?.data
  const locked = execution?.status === 'locked'
  if (!execution) {
    return <p className="p-4 text-sm text-muted-foreground">Select an area to build its checklist.</p>
  }
  const checklistId = workspace.allChecklists ? null : workspace.activeChecklistId
  const onUndo = controller.history.canUndo ? () => { void controller.history.undo() } : undefined
  const onRedo = controller.history.canRedo ? () => { void controller.history.redo() } : undefined
  const itemSelection = {
    isSelected: (id: string, checklistId?: string) => selection.isCheckIncluded(id, checklistId, execution.id),
    onToggle: (id: string, checklistId?: string) => selection.toggleCheck(id, checklistId, execution.id),
  }

  const insertLibrary = checklistId && controller.canBuild !== false
    ? () => controller.requestOpenTemplates({
      checklistId,
      areaId: execution.id,
      name: execution.checklists.find((row) => row.id === checklistId)?.template_name ?? 'checklist',
    })
    : undefined

  if (view === 'cards') {
    return (
      <QaPhoneBuildCards
        area={execution}
        api={api}
        client={client}
        disabled={locked}
        checklistId={checklistId}
        onMutated={workspace.invalidateExecution}
        itemSelection={itemSelection}
        showAddRow={controller.canBuild !== false}
        onUndo={onUndo}
        onRedo={onRedo}
        checklistMenuItems={checklistMenuItems}
        onInsertFromLibrary={insertLibrary}
      />
    )
  }

  return (
    <div
      data-qa-phone-control={[
        QA_PHONE_CONTROLS.addRow,
        QA_PHONE_CONTROLS.rowActions,
        QA_PHONE_CONTROLS.tableColumns,
        QA_PHONE_CONTROLS.hold,
        QA_PHONE_CONTROLS.parked,
        QA_PHONE_CONTROLS.sectionCollapse,
      ].join(' ')}
    >
      <QaChecklistTable
        area={execution}
        api={api}
        disabled={locked}
        scrollClassName="overflow-x-auto"
        checklistId={checklistId}
        onMutated={workspace.invalidateExecution}
        onRenameChecklist={locked ? undefined : (id, title) => {
          void api.renameChecklist(id, title).then(workspace.invalidateExecution)
        }}
        itemSelection={itemSelection}
        checklistSelection={{
          isSelected: (id) => selection.isSelected('checklists', id),
          onToggle: (id) => selection.toggle('checklists', id),
        }}
        onUndo={onUndo}
        onRedo={onRedo}
        checklistMenuItems={checklistMenuItems}
        onInsertFromLibrary={insertLibrary}
        startRenameId={renameChecklistId ?? null}
        onRenameStarted={onRenameChecklistStarted}
      />
    </div>
  )
}

export function QaPhoneTableView(props: { controller: JobQaTabController }) {
  return <QaPhoneBuildList {...props} />
}
